import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import { IconChevronLeft, IconDownload, IconLock, IconPrinter, IconSend } from '@tabler/icons-react';
import { Page, PageHeader } from '@hana/shell';
import { Skeleton, StateView, useToast } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { PH_DATA, PH_TABS, padF1, phAmt, phLines, phMain, phSecond, phShort, type PhRow, type PhTab, type PhTone } from './invm';
import './invphone.i18n';
import s from './invm.module.css';

// Mint-Invoice-Phone (390): khung 1 = 6 tab + thẻ chứng từ · khung 2 = chi tiết (đối soát phí job · dòng hoá đơn · thao tác nổi). TIỀN → sau quyền `money`. Chỉ đọc; nút ghi báo toast.
const NOTE: Readonly<Record<PhTone, string>> = { ok: s.tOk ?? '', warn: s.tWarn ?? '', err: s.tErr ?? '', info: s.tInfo ?? '' };
const RECS: readonly (readonly [string, string])[] = [['THC lệch giá với phí job', 'Đồng bộ'], ['DEM 2 ngày chưa lên debit', 'Debit bổ sung']]; // dữ liệu mẫu (không dịch)

export function InvoicePhone() {
  const t = useT();
  const toast = useToast();
  const access = useFinanceAccess();
  const state = financeScreenState(access);
  const [tab, setTab] = useState<PhTab>('issue');
  const [open, setOpen] = useState<string | null>(null);
  const noWrite = () => toast({ msg: t('iph.noWrite') });
  const m = (x: string) => (access.money ? x : '•••••');
  const list = PH_DATA[tab];
  const sum = useMemo(() => list.reduce((a, x) => a + x.amt, 0), [list]);
  const cur: PhRow | undefined = open ? list.find((x) => x.id === open) : undefined;

  if (cur && state === 'ready') {
    const { lines, net, vat, gross } = phLines(cur);
    const locked = !!cur.lock;
    const rec = tab === 'dcf' || tab === 'issue';
    return (
      <Page kind="table">
        <PageHeader title={cur.id} findHint={false} noFind />
        <div className={s.phone}>
          <div className={s.dh}>
            <button type="button" className={s.ib} aria-label={t('iph.back')} onClick={() => setOpen(null)}><IconChevronLeft size={22} aria-hidden="true" /></button>
            <div><small>{t(`iph.kind.${tab}`)}</small><b>{cur.id}</b></div>
            <button type="button" className={s.ib} aria-label={t('iph.pdf')} onClick={() => toast({ msg: t('iph.pdfOff') })}><IconDownload size={19} aria-hidden="true" /></button>
          </div>
          <div className={`${s.nt} ${NOTE[cur.tone]}`}>{cur.st} · {cur.sub}</div>
          {rec && (
            <div className={s.sec}>
              <div className={s.sh}><b>{t('iph.recon')}</b><span className={`${s.chip} ${locked ? s.tOk : s.tWarn}`}>{tab === 'issue' && cur.id !== 'DN26054305' ? t('iph.locked') : t('iph.unlocked')}</span></div>
              {RECS.map(([k, act]) => <div key={k} className={s.rec}><span>{k}</span><button type="button" onClick={noWrite}>{act}</button></div>)}
            </div>
          )}
          {tab !== 'queue' && (
            <section className={s.sec} aria-label={t('iph.lines')}>
              <div className={s.sh}><b>{t('iph.lines')}</b><span>{tab === 'out' ? t('iph.vatInv') : tab === 'adj' ? t('iph.adjInv') : cur.amt < 0 ? 'CREDIT NOTE' : 'DEBIT NOTE'}</span></div>
              {lines.map(([k, v, n]) => <div key={k} className={s.ln}><span className={s.ell}>{k}</span><span>{v}</span><span>{m(n)}</span></div>)}
              <div className={s.ln}><span className={s.ell} title={t('iph.advTip')}>{t('iph.adv')}</span><span>—</span><span>{m('1.250.000')}</span></div>
              <div className={s.tt}><span>{t('iph.net')}</span><span>{m(net)}</span><span>{t('iph.vat')}</span><span>{m(vat)}</span><b className={s.g}>{t('iph.gross')}</b><b className={s.g}>{m(gross)}</b></div>
              <div className={s.pr}><span>{t('iph.fx', { r: '25.400' })} · {tab === 'out' ? t('iph.signed') : t('iph.unsigned')}</span><button type="button" className={s.prb} onClick={() => toast({ msg: t('iph.printOff') })}><IconPrinter size={16} aria-hidden="true" />{t('iph.print')}</button></div>
            </section>
          )}
          {locked && <div className={s.sec}>{t('iph.periodLocked', { p: '09/2026' })}</div>}
          <div className={s.fab}>
            <button type="button" className={s.s} onClick={noWrite}>{phSecond(tab)}</button>
            <button type="button" className={s.m} disabled={locked} onClick={noWrite}>{locked ? <IconLock size={16} aria-hidden="true" /> : <IconSend size={16} aria-hidden="true" />}{phMain(tab, locked)}</button>
          </div>
        </div>
      </Page>
    );
  }

  return (
    <Page kind="table">
      <PageHeader title={t('iph.title')} findHint={false} />
      <div className={s.phone}>
        <div className={s.ptabs} role="tablist" aria-label={t('iph.groups')}>
          {PH_TABS.map(([k]) => <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>{t(`iph.tab.${k}`)}{state === 'ready' && ` ${PH_DATA[k].length}`}</button>)}
        </div>
        <StateView state={state} skeleton={<Skeleton rows={5} />}>
          <>
            <div className={s.note}><span>{t('iph.docs', { n: list.length })}</span><span>{tab === 'queue' ? '' : m(`${padF1(sum)} tr`)}</span></div>
            {list.map((x) => (
              <button key={x.id} type="button" className={s.pc} aria-label={`${x.id} ${phAmt(tab, x)} ${x.cus} ${phShort(x.st)} ${x.sub}`} onClick={() => setOpen(x.id)}>
                <span className={s.t}><b>{x.id}</b><b>{tab === 'queue' ? phAmt(tab, x) : m(phAmt(tab, x))}</b></span>
                <span className={s.m}><span>{x.cus}</span><span className={`${s.chip} ${NOTE[x.tone]}`}><i aria-hidden="true" /><span title={x.st} aria-label={x.st}>{phShort(x.st)}</span></span></span>
                <span className={s.sb}>{x.sub}</span>
              </button>
            ))}
          </>
        </StateView>
      </div>
    </Page>
  );
}
